/**
 * Loading state helpers and formatters for app-wide use
 *
 * This module provides utilities for handling loading states and formatting loading values:
 * - `LoadingState` interface for standardized loading configurations
 * - `formatLoadingValue()` for displaying animated numeric values during loading
 * - `generateRandomValue()` for creating realistic loading animations
 * - Helper functions for determining loading animation behavior
 *
 * Usage:
 * ```tsx
 * // Basic loading state
 * const loadingState: LoadingState = {
 *   isLoading: true,
 *   animationType: LoadingAnimationType.DIGIT,
 *   dataType: DataDisplayType.PERCENTAGE,
 *   duration: 2000
 * }
 *
 * // Format a loading value
 * const formattedValue = formatLoadingValue({
 *   type: DataDisplayType.CURRENCY,
 *   value: 1234.56
 * }) // Returns: "$1,234.56"
 *
 * // Component with loading state
 * <StatCard
 *   label="Total Value"
 *   value="$123,456"
 *   loadingState={loadingState}
 * />
 * ```
 */
'use client'

import {
  DataDisplayType,
  LoadingAnimationType,
} from '@/shared/constants/animations'

// Data type configuration interfaces and constants
interface DataTypeConfig {
  minValue: number
  maxValue: number
  decimalPlaces: number
  suffix?: string
}

// Configuration for different data types
const DATA_TYPE_CONFIGS: Record<DataDisplayType, DataTypeConfig> = {
  [DataDisplayType.PERCENTAGE]: {
    minValue: 10,
    maxValue: 100,
    decimalPlaces: 2,
    suffix: '%',
  },
  [DataDisplayType.YEAR]: {
    minValue: 10,
    maxValue: 20,
    decimalPlaces: 0,
    suffix: ' yrs',
  },
  [DataDisplayType.CURRENCY]: {
    minValue: 1000,
    maxValue: 500000,
    decimalPlaces: 2,
  },
  [DataDisplayType.DECIMAL]: {
    minValue: 0,
    maxValue: 100,
    decimalPlaces: 2,
  },
  [DataDisplayType.INTEGER]: {
    minValue: 0,
    maxValue: 999,
    decimalPlaces: 0,
  },
}

// Loading state interfaces
/**
 * Standard loading state configuration for components
 */
export interface LoadingState {
  /** Whether the component is in a loading state */
  isLoading: boolean
  /** Type of loading animation to display */
  animationType?: LoadingAnimationType
  /** For numeric values, the data type to animate */
  dataType?: DataDisplayType
  /** Custom loading text or placeholder */
  loadingText?: string
  /** Duration of the loading animation in ms */
  duration?: number
  /** Skeleton width for text elements */
  skeletonWidth?: string
}

// Formatting functions
interface FormatValueOptions {
  type: DataDisplayType
  value: number
  decimalPlaces?: number
  prefix?: string
  suffix?: string
}

/**
 * Formats a numeric value according to its display type
 */
export function formatLoadingValue({
  type,
  value,
  decimalPlaces,
  prefix = '',
  suffix = '',
}: FormatValueOptions): string {
  // Guard against undefined/null/NaN values
  // biome-ignore lint/suspicious/noGlobalIsNan: Bulk suppression as part of biome migration
  if (value === undefined || value === null || isNaN(value)) {
    return `${prefix}0${suffix}`
  }

  const config = DATA_TYPE_CONFIGS[type]
  const decimals = decimalPlaces ?? config.decimalPlaces
  const finalSuffix = suffix || config.suffix || ''

  switch (type) {
    case DataDisplayType.CURRENCY:
      return `${prefix}$${value.toLocaleString('en-US', {
        minimumFractionDigits: decimals,
        maximumFractionDigits: decimals,
      })}${finalSuffix}`

    case DataDisplayType.PERCENTAGE:
      return `${prefix}${value.toFixed(decimals)}${finalSuffix}`

    case DataDisplayType.DECIMAL:
      return `${prefix}${value.toFixed(decimals)}${finalSuffix}`

    case DataDisplayType.INTEGER:
      return `${prefix}${Math.floor(value).toLocaleString()}${finalSuffix}`

    case DataDisplayType.YEAR:
      return `${prefix}${Math.floor(value).toLocaleString()}${finalSuffix}`

    default:
      return `${prefix}${value.toLocaleString()}${finalSuffix}`
  }
}

/**
 * Gets the configuration for a specific data type
 */
export function getDataTypeConfig(
  type: DataDisplayType,
  overrides?: Partial<DataTypeConfig>,
): DataTypeConfig {
  return {
    ...DATA_TYPE_CONFIGS[type],
    ...overrides,
  }
}

/**
 * Generates a random value within the bounds of a data type
 */
export function generateRandomValue(
  type: DataDisplayType,
  minValue?: number,
  maxValue?: number,
): number {
  const config = DATA_TYPE_CONFIGS[type]
  const min = minValue ?? config.minValue
  const max = maxValue ?? config.maxValue

  if (type === DataDisplayType.PERCENTAGE || type === DataDisplayType.DECIMAL) {
    return Math.random() * (max - min) + min
  }

  return Math.floor(Math.random() * (max - min + 1)) + min
}

// Loading state helper functions
/**
 * Helper to determine if a component should show loading animation
 */
export function shouldShowLoadingAnimation(
  loadingState?: LoadingState,
  defaultAnimation: LoadingAnimationType = LoadingAnimationType.SKELETON,
): boolean {
  if (!loadingState?.isLoading) return false
  return (
    (loadingState.animationType ?? defaultAnimation) !==
    LoadingAnimationType.NONE
  )
}
